import React from 'react'
import styled from 'styled-components'
import { Button } from '@streamr/streamr-layout'
import { TABLET, DESKTOP } from '@streamr/streamr-layout/styled'
import downloadFile from './assets/download_file.png'
import downloadFile2x from './assets/download_file@2x.png'
import Topic from '~shared/Topic'
import Display from '~shared/Display'
import Br from '~shared/Br'

const Text = styled.div`
    @media (min-width: ${TABLET}px) {
        ${Topic.Paragraph} {
            font-size: 1rem;
            line-height: 1.75rem;
        }
    }

    @media (min-width: ${DESKTOP}px) {
        ${Topic.Paragraph} {
            font-size: 1.125rem;
            line-height: 1.875rem;
        }
    }
`

const Icon = styled.div`
    img {
        max-width: 92px;
    }

    @media (min-width: ${TABLET}px) {
        img {
            max-width: 108px;
        }
    }

    @media (min-width: ${DESKTOP}px) {
        img {
            max-width: 148px;
        }
    }
`

const DownloadInfo = styled.div`
    font-size: 0.75rem;
    line-height: 1.25rem;
`

const DownloadButton = styled.div`
    ${Button} {
        min-height: 56px;
        width: 100%;
    }

    @media (min-width: ${TABLET}px) {
        ${Button} {
            width: auto;
        }
    }

    @media (min-width: ${DESKTOP}px) {
        ${Button} {
            min-height: 48px;
            width: 100%;
        }
    }
`

const Actions = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    grid-column-gap: 1.5rem;
    grid-row-gap: 2.5rem;
    padding: 0 1rem;

    ${Icon} {
        grid-row: 1;
        grid-column: 1;
        text-align: center;
        margin-left: -1rem;
    }

    ${DownloadButton} {
        grid-row: 2;
        grid-column: 1;
    }

    ${DownloadInfo} {
        grid-row: 3;
        grid-column: 1;
    }

    @media (min-width: ${TABLET}px) {
        grid-template-columns: 108px 264px;
        grid-row-gap: 1rem;
        grid-column-gap: 2.75rem;
        max-width: none;
        padding: 0;

        ${Icon} {
            grid-row: 1 / 3;
            grid-column: 1;
            margin-top: -10px;
            margin-left: 0;
            text-align: left;
        }

        ${DownloadButton} {
            grid-row: 1;
            grid-column: 2;
        }

        ${DownloadInfo} {
            grid-row: 2;
            grid-column: 2;
        }
    }

    @media (min-width: ${DESKTOP}px) {
        grid-template-columns: 1fr;
        grid-row-gap: 1.5rem;
        max-width: 216px;

        ${Icon} {
            display: block;
            grid-row: 1;
            grid-column: 1;
            margin: 0 0 1rem 1rem;
        }

        ${DownloadInfo} {
            grid-row: 3;
            grid-column: 1;
        }

        ${DownloadButton} {
            grid-row: 2;
            grid-column: 1;
        }
    }
`

const Inner = styled.div`
    margin: 0 auto;
    padding: 2.5rem 1.5rem 4.5rem;
    background-color: #FAFAFA;
    border-radius: 8px;
    color: #0C009A;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;

    ${Text} + ${Actions} {
        margin-top: 3.5rem;
    }

    ${Topic.Title} {
        margin: 0 0 2rem 0;
        text-align: left;
    }

    @media (min-width: ${TABLET}px) {
        padding: 5rem 5.25rem 5.5rem 6rem;
        max-width: 704px;

        ${Text} + ${Actions} {
            margin-top: 4.5rem;
        }
    }

    @media (min-width: ${DESKTOP}px) {
        max-width: 1160px;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        padding: 7rem 8rem 8.5rem 7.5rem;

        ${Text} + ${Actions} {
            margin-top: 0;
        }

        ${Text} {
            max-width: 514px;
        }
    }
`

const UnstyledWhyBuild = (props) => (
    <div {...props}>
        <Inner $appearDirection="none" $appearIntersection>
            <Text>
                <Display as={Topic.Title} mobile="none" tablet="block" hash="why-build" $appear>
                    Why build a Data Union?
                </Display>
                <Display as={Topic.Title} mobile="block" tablet="none" hash="why-build" $appear>
                    Why build a DU?
                </Display>
                <Topic.Paragraph $appear>
                    The data economy needs fixing. Trust between those who
                    generate data and those who sell and control is at an all-time
                    low. Data Unions help you work cooperatively with your users
                    instead of against them, with a brand new incentive model for
                    crowdsourcing real-time data.
                </Topic.Paragraph>
                <Topic.Paragraph $appear>
                    The Data Union framework provides a simple front end to use
                    Ethereum smart contracts and the xDai sidechain to broadcast revenue to large groups
                    of users simultaneously.
                </Topic.Paragraph>
                <Display as={Topic.Paragraph} mobile="none" tablet="block" $appear>
                    This is an entirely new, fair and ethical solution to the problem
                    of incentivising the creation of large and valuable datasets.
                    Streamr commissioned market research that indicated widespread interest
                    <Br tablet />
                    in the idea.
                </Display>
            </Text>
            <Actions $appear>
                <DownloadButton>
                    <Button
                        tag="a"
                        kind="primary"
                        size="big"
                        href="/resources/data_unions_research_report.pdf"
                    >
                        Download report
                    </Button>
                </DownloadButton>
                <DownloadInfo>
                    Download an executive summary of
                    <Br desktop />
                    the research into attitudes towards
                    <Br desktop />
                    Data Unions (PDF).
                </DownloadInfo>
                <Icon>
                    <img src={downloadFile} srcSet={downloadFile2x} alt="Download" />
                </Icon>
            </Actions>
        </Inner>
    </div>
)

const WhyBuild = styled(UnstyledWhyBuild)`
    padding: 0 1rem;

    @media (min-width: ${TABLET}px) {
        padding: 0 2rem;
    }

    @media (min-width: ${DESKTOP}px) {
        padding: 0;
    }
`

export default WhyBuild
